import React, { useState } from 'react';
import {
  Box, Typography, Button, CircularProgress,
  FormControl, RadioGroup, Paper, Alert, useMediaQuery, useTheme,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const C = {
  navy:      '#273F77',
  orange:    '#E79A2B',
  bg:        '#f5f6fa',
  cardBg:    '#ffffff',
  border:    '#e8eaf0',
  textMuted: '#8492a6',
};

const TYPE_STYLE = {
  MCQ:          { bg: 'rgba(33,150,243,0.1)',  color: '#1565c0' },
  Short:        { bg: 'rgba(255,152,0,0.1)',   color: '#e65100' },
  'True/False': { bg: 'rgba(76,175,80,0.1)',   color: '#2e7d32' },
};

// ─── Option row ───────────────────────────────────────────────────────────────
const OptionRow = ({ optionKey, optionVal, selected, onClick, type }) => (
  <Box
    onClick={onClick}
    sx={{
      display: 'flex', alignItems: 'center', gap: 1.5,
      border: '1.5px solid',
      borderColor: selected ? C.navy : C.border,
      borderRadius: '12px',
      px: { xs: 1.5, sm: 2 },
      py: { xs: 1, sm: 1.3 },
      mb: 1,
      cursor: 'pointer',
      transition: 'all 0.15s',
      backgroundColor: selected ? 'rgba(39,63,119,0.05)' : C.cardBg,
      '&:hover': { borderColor: C.navy, backgroundColor: 'rgba(39,63,119,0.03)' },
    }}
  >
    <Box sx={{
      width: 20, height: 20, borderRadius: '50%', flexShrink: 0,
      border: '2px solid',
      borderColor: selected ? C.navy : '#c9d0d8',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      backgroundColor: selected ? C.navy : 'transparent',
      transition: 'all 0.15s',
    }}>
      {selected && <Box sx={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: '#fff' }} />}
    </Box>
    <Typography variant="body2" fontWeight={selected ? 600 : 400} color="text.primary">
      {type === 'MCQ' ? `${optionKey}. ${optionVal}` : optionVal}
    </Typography>
  </Box>
);

// ─── Question block ───────────────────────────────────────────────────────────
const QuestionBlock = ({ q, index, answer, onAnswer }) => {
  const getOptions = () => {
    if (q.type === 'True/False') return { True: 'True', False: 'False' };
    return q.options || {};
  };
  const style = TYPE_STYLE[q.type] || { bg: '#f5f5f5', color: '#616161' };

  return (
    <Paper
      id={`question-${q.id}`}
      elevation={0}
      sx={{
        borderRadius: '16px',
        border: `1.5px solid ${answer ? C.navy : C.border}`,
        p: { xs: 2, sm: 3 },
        mb: 2,
        backgroundColor: C.cardBg,
        transition: 'border-color 0.2s',
      }}
    >
      <Box display="flex" alignItems="flex-start" gap={{ xs: 1.5, sm: 2 }} mb={2}>
        {/* Number badge */}
        <Box sx={{
          width: { xs: 30, sm: 36 },
          height: { xs: 30, sm: 36 },
          borderRadius: '10px', flexShrink: 0,
          backgroundColor: answer ? C.navy : C.bg,
          color: answer ? '#fff' : C.textMuted,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontWeight: 800, fontSize: '0.8rem',
          transition: 'all 0.2s',
        }}>
          {answer ? <CheckCircleIcon sx={{ fontSize: { xs: 15, sm: 18 } }} /> : index + 1}
        </Box>

        <Box flex={1}>
          <Box sx={{
            display: 'inline-flex', alignItems: 'center',
            px: 1.25, py: 0.25, borderRadius: '20px', mb: 0.75,
            backgroundColor: style.bg,
          }}>
            <Typography variant="caption" fontWeight={700} color={style.color}
              textTransform="uppercase" letterSpacing={0.4} lineHeight={1.6}>
              {q.type}
            </Typography>
          </Box>
          <Typography
            variant="body1"
            fontWeight={600}
            color="text.primary"
            lineHeight={1.5}
            fontSize={{ xs: '0.9rem', sm: '1rem' }}
          >
            {q.question}
          </Typography>
        </Box>
      </Box>

      <FormControl fullWidth>
        <RadioGroup value={answer || ''} onChange={(e) => onAnswer(q.id, e.target.value)}>
          {Object.entries(getOptions()).map(([key, val]) => (
            <OptionRow
              key={key} optionKey={key} optionVal={val}
              selected={answer === key}
              onClick={() => onAnswer(q.id, key)}
              type={q.type}
            />
          ))}
        </RadioGroup>
      </FormControl>
    </Paper>
  );
};

// ─── Desktop sidebar nav dot ──────────────────────────────────────────────────
const NavDot = ({ index, answered, onClick }) => (
  <Box
    onClick={onClick}
    title={`Q${index + 1}`}
    sx={{
      width: 36, height: 36, borderRadius: '10px',
      border: '1.5px solid',
      borderColor: answered ? C.navy : C.border,
      backgroundColor: answered ? C.navy : C.cardBg,
      color: answered ? '#fff' : C.textMuted,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer',
      transition: 'all 0.15s',
      '&:hover': { borderColor: C.navy, transform: 'scale(1.1)' },
    }}
  >
    {answered ? <CheckCircleIcon sx={{ fontSize: 16 }} /> : index + 1}
  </Box>
);

// ─── Mobile bottom bar ────────────────────────────────────────────────────────
const MobileBottomBar = ({ questions, answers, answeredCount, allAnswered, submitting, onSubmit, scrollTo }) => (
  <Box sx={{
    position: 'fixed', bottom: 75, left: 0, right: 0, zIndex: 100,
    backgroundColor: C.cardBg,
    borderTop: `1px solid ${C.border}`,
    borderBottom: `1px solid ${C.border}`,
    px: 2, py: 1.5,
    boxShadow: '0 -4px 16px rgba(0,0,0,0.08)',
  }}>
    {/* Progress bar */}
    <Box display="flex" alignItems="center" gap={1.5} mb={1.25}>
      <Box flex={1} sx={{ height: 6, borderRadius: 99, backgroundColor: C.border, overflow: 'hidden' }}>
        <Box sx={{
          height: '100%', borderRadius: 99,
          width: `${(answeredCount / questions.length) * 100}%`,
          backgroundColor: allAnswered ? '#2e7d32' : C.orange,
          transition: 'width 0.3s ease',
        }} />
      </Box>
      <Typography variant="caption" fontWeight={700} color={C.navy} minWidth={40} textAlign="right">
        {answeredCount}/{questions.length}
      </Typography>
    </Box>

    {/* Nav dots scrollable row */}
    <Box
      display="flex"
      gap={0.75}
      mb={1.25}
      sx={{ overflowX: 'auto', pb: 0.5, '&::-webkit-scrollbar': { display: 'none' } }}
    >
      {questions.map((q, i) => (
        <Box
          key={q.id}
          onClick={() => scrollTo(q.id)}
          sx={{
            width: 30, height: 30, borderRadius: '8px', flexShrink: 0,
            border: '1.5px solid',
            borderColor: answers[q.id] ? C.navy : C.border,
            backgroundColor: answers[q.id] ? C.navy : C.cardBg,
            color: answers[q.id] ? '#fff' : C.textMuted,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '0.7rem', fontWeight: 700, cursor: 'pointer',
          }}
        >
          {answers[q.id] ? <CheckCircleIcon sx={{ fontSize: 13 }} /> : i + 1}
        </Box>
      ))}
    </Box>

    {/* Submit button */}
    <Button
      fullWidth variant="contained"
      onClick={onSubmit}
      disabled={!allAnswered || submitting}
      startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}
      sx={{
        backgroundColor: allAnswered ? C.orange : '#e0e0e0',
        color: allAnswered ? '#fff' : '#9e9e9e',
        borderRadius: '10px', textTransform: 'none',
        fontWeight: 700, py: 1.2, fontSize: '0.9rem',
        boxShadow: allAnswered ? '0 4px 14px rgba(231,154,43,0.4)' : 'none',
        '&:hover': { backgroundColor: allAnswered ? '#d4891f' : '#e0e0e0' },
      }}
    >
      {submitting ? 'Submitting…'
        : allAnswered ? 'Submit Quiz'
        : `${questions.length - answeredCount} remaining`}
    </Button>
  </Box>
);

// ─── Main ─────────────────────────────────────────────────────────────────────
const QuizQuestions = ({ questions, onSubmit, submitting }) => {
  const [answers, setAnswers] = useState({});
  const theme   = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const answeredCount = Object.keys(answers).length;
  const allAnswered   = answeredCount === questions.length;

  const handleAnswer = (id, val) => setAnswers((prev) => ({ ...prev, [id]: val }));

  const scrollTo = (id) => {
    document.getElementById(`question-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const handleSubmit = () => {
    onSubmit(Object.entries(answers).map(([qid, ua]) => ({
      question_id: parseInt(qid),
      user_answer: ua,
    })));
  };

  return (
    <>
      <Box
        display="flex"
        gap={3}
        alignItems="flex-start"
        maxWidth={1000}
        mx="auto"
        // Extra bottom padding on mobile so content isn't hidden behind fixed bottom bar
        pb={{ xs: '240px', sm: 0 }}
      >
        {/* ── Questions column ── */}
        <Box flex={1}>
          {/* Progress row — desktop only (mobile uses bottom bar) */}
          {/* <Paper elevation={0} sx={{
            borderRadius: '16px',
            border: `1px solid ${C.border}`,
            p: { xs: 1.5, sm: 2 },
            mb: 2.5,
            display: 'flex', alignItems: 'center', gap: 2,
          }}> */}
            {/* <Box flex={1}> */}
              {/* <Box display="flex" justifyContent="space-between" mb={0.75}>
                <Typography variant="body2" fontWeight={600} color={C.textMuted}
                  fontSize={{ xs: '0.8rem', sm: '0.875rem' }}>
                  Progress11
                </Typography>
                <Typography variant="body2" fontWeight={700} color={C.navy}
                  fontSize={{ xs: '0.8rem', sm: '0.875rem' }}>
                  {answeredCount} / {questions.length} answered
                </Typography>
              </Box> */}
              {/* <Box sx={{ height: 8, borderRadius: 99, backgroundColor: C.border, overflow: 'hidden' }}>
                <Box sx={{
                  height: '100%', borderRadius: 99,
                  width: `${(answeredCount / questions.length) * 100}%`,
                  backgroundColor: allAnswered ? '#2e7d32' : C.orange,
                  transition: 'width 0.3s ease',
                }} />
              </Box> */}
            {/* </Box> */}
            {/* <Typography variant="h6" fontWeight={800} color={C.navy} minWidth={44} textAlign="right"
              fontSize={{ xs: '1rem', sm: '1.25rem' }}>
              {Math.round((answeredCount / questions.length) * 100)}%
            </Typography> */}
          {/* </Paper> */}

          {!allAnswered && (
            <Alert
              severity="info"
              sx={{ mb: 2, borderRadius: '12px', border: `1px solid rgba(33,150,243,0.2)` }}
            >
              Answer all {questions.length} questions before submitting.
            </Alert>
          )}

          {questions.map((q, i) => (
            <QuestionBlock key={q.id} q={q} index={i} answer={answers[q.id]} onAnswer={handleAnswer} />
          ))}

          {/* Submit button — desktop only */}
          {!isMobile && (
            <Button
              fullWidth variant="contained" size="large"
              onClick={handleSubmit}
              disabled={!allAnswered || submitting}
              startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : null}
              sx={{
                mt: 1,
                backgroundColor: allAnswered ? C.orange : '#e0e0e0',
                color: allAnswered ? '#fff' : '#9e9e9e',
                borderRadius: '12px', textTransform: 'none',
                fontWeight: 700, py: 1.6, fontSize: '1rem',
                boxShadow: allAnswered ? '0 4px 14px rgba(231,154,43,0.4)' : 'none',
                '&:hover': {
                  backgroundColor: allAnswered ? '#d4891f' : '#e0e0e0',
                  boxShadow: allAnswered ? '0 6px 18px rgba(231,154,43,0.5)' : 'none',
                },
                transition: 'all 0.2s',
              }}
            >
              {submitting ? 'Submitting…'
                : allAnswered ? 'Submit Quiz'
                : `${questions.length - answeredCount} question${questions.length - answeredCount !== 1 ? 's' : ''} remaining`}
            </Button>
          )}
        </Box>

        {/* ── Sticky sidebar — desktop only ── */}
        {!isMobile && (
          <Paper
            elevation={0}
            sx={{
              width: 200, flexShrink: 0,
              position: 'sticky', top: 76,
              borderRadius: '16px',
              border: `1px solid ${C.border}`,
              p: 2,
            }}
          >
            <Typography variant="caption" color={C.textMuted} fontWeight={700}
              textTransform="uppercase" letterSpacing={0.6} display="block" mb={1.5}>
              Jump to
            </Typography>

            <Box display="flex" flexWrap="wrap" gap={0.75} mb={2}>
              {questions.map((q, i) => (
                <NavDot key={q.id} index={i} answered={!!answers[q.id]} onClick={() => scrollTo(q.id)} />
              ))}
            </Box>

            <Box sx={{ height: 1, backgroundColor: C.border, mb: 1.5 }} />

            <Box display="flex" gap={0.75} alignItems="center" mb={0.75}>
              <Box sx={{ width: 12, height: 12, borderRadius: '3px', backgroundColor: C.navy }} />
              <Typography variant="caption" color={C.textMuted}>Answered</Typography>
            </Box>
            <Box display="flex" gap={0.75} alignItems="center" mb={2}>
              <Box sx={{ width: 12, height: 12, borderRadius: '3px', border: `1.5px solid ${C.border}` }} />
              <Typography variant="caption" color={C.textMuted}>Unanswered</Typography>
            </Box>

            <Box sx={{ height: 1, backgroundColor: C.border, mb: 1.5 }} />
            <Box display="flex" justifyContent="space-between">
              <Typography variant="caption" color={C.textMuted} fontWeight={600}>Done</Typography>
              <Typography variant="caption" color={C.navy} fontWeight={700}>{answeredCount}/{questions.length}</Typography>
            </Box>
            <Box sx={{ height: 4, borderRadius: 99, backgroundColor: C.border, mt: 0.75 }}>
              <Box sx={{
                height: '100%', borderRadius: 99,
                width: `${(answeredCount / questions.length) * 100}%`,
                backgroundColor: allAnswered ? '#2e7d32' : C.orange,
                transition: 'width 0.3s ease',
              }} />
            </Box>
          </Paper>
        )}
      </Box>

      {/* ── Mobile bottom bar ── */}
      {isMobile && (
        <MobileBottomBar
          questions={questions}
          answers={answers}
          answeredCount={answeredCount}
          allAnswered={allAnswered}
          submitting={submitting}
          onSubmit={handleSubmit}
          scrollTo={scrollTo}
        />
      )}
    </>
  );
};

export default QuizQuestions;